import React, { useEffect, useRef, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../shadcn-ui/drawer';
import { Button } from '../shadcn-ui/button';
import FormField from '../FormField';
import { yupResolver } from '@hookform/resolvers/yup';
import { ProcessCheckSchema } from '@/schema/process-lot-schema';
import { useFieldArray, useForm } from 'react-hook-form';
import { Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { deleteMedia, insertOrUpdateFermentationProcessCheck } from '@/network/api.service';
import { useMediaUploader } from '@/hooks/useMediaUploader';
import MediaUploader from './MediaUploader';

const FermentationProcessCheckDrawer = ({
  lotId,
  processStepId,
  onSuccess,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  checkData,
  onClose,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mediaToRemove, setMediaToRemove] = useState([]);

  const uploadRef = useRef(null);
  const media = useMediaUploader({ folder: 'lot-fermentation-process-check-media' });

  // Use controlled open if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  const handleOpenChange = newOpen => {
    if (loading) return;
    setOpen(newOpen);
    if (!newOpen && onClose) {
      onClose();
    }
    if (newOpen) {
      // Blur any focused elements in the background when drawer opens
      const activeElement = document.activeElement;
      if (activeElement && activeElement instanceof HTMLElement) {
        activeElement.blur();
      }
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(ProcessCheckSchema),
    defaultValues: {
      time_of_check: new Date(),
    },
  });

  const { fields, remove } = useFieldArray({
    control,
    name: 'media',
  });

  // Populate form when checkData changes (editing mode)
  useEffect(() => {
    if (checkData && open) {
      reset({
        time_of_check: checkData.time_of_check ? new Date(checkData.time_of_check) : new Date(),
        was_coffee_turned: checkData.was_coffee_turned ?? null,
        ph_value: checkData.ph_value ?? '',
        temperature: checkData.temperature ?? '',
        notes: checkData.notes ?? '',
        media: checkData.media ?? [],
      });
    } else if (!checkData && open) {
      // Reset to defaults when adding new check
      reset({
        time_of_check: new Date(),
        was_coffee_turned: null,
        ph_value: '',
        temperature: '',
        notes: '',
      });
    }
  }, [checkData, open, reset]);

  const onSubmit = async data => {
    setLoading(true);

    if (mediaToRemove.length > 0) {
      await deleteMedia(mediaToRemove.map(m => m.path));
      setMediaToRemove([]);
    }

    if (uploadRef.current) {
      await uploadRef.current();
    }
    const uploaded = await media.uploadAll();
    const existingMedia = Array.isArray(data.media) ? data.media : [];

    const mergedMedia = uploaded && uploaded.length > 0 ? [...existingMedia, ...uploaded] : existingMedia;

    const body = {
      ...data,
      media: mergedMedia,
      ...(checkData?._id ? { check_id: checkData._id } : {}),
    };

    try {
      const respData = await insertOrUpdateFermentationProcessCheck(body, lotId, processStepId);
      if (respData?.data?.success) {
        toast.success('Check saved successfully.');
        setOpen(false);
        reset();
        media.setLocalMedia([]);
        media.setUploadedMedia([]);
        // Refresh the data in the parent component
        if (onSuccess) {
          onSuccess();
        }
        if (onClose) {
          onClose();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save check');
    } finally {
      setLoading(false);
    }
  };
  // const onSubmit = async data => {
  //   setLoading(true);
  //   const body = {
  //     ...data,
  //     ...(checkData?._id ? { check_id: checkData._id } : {}),
  //   };

  //   try {
  //     const respData = await insertOrUpdateFermentationProcessCheck(body, lotId, processStepId);
  //     console.log(respData);
  //     if (respData?.data?.success) {
  //       toast.success('Check saved successfully.');
  //       setOpen(false);
  //       reset();
  //       // Refresh the data in the parent component
  //       if (onSuccess) {
  //         onSuccess();
  //       }
  //       if (onClose) {
  //         onClose();
  //       }
  //     }
  //   } catch (error) {
  //     toast.error(error.response?.data?.message || 'Failed to save check');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const onRemoveMedia = file => {
    setMediaToRemove(prev => [...prev, file]);
    const index = fields.findIndex(m => m._id === file._id);

    if (index !== -1) {
      remove(index);
    }
  };

  const handleAddClick = () => {
    if (onClose) {
      onClose(); // Clear editing state
    }
    setOpen(true);
  };

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild>
        <Button className="w-full border-dashed" variant="outline" onClick={handleAddClick}>
          <Plus size={16} className="mr-2" />
          Add Check
        </Button>
      </DrawerTrigger>

      <DrawerContent
        className="max-h-[90%]"
        onEscapeKeyDown={e => loading && e.preventDefault()}
        onPointerDownOutside={e => loading && e.preventDefault()}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="max-h-[90%] overflow-y-scroll">
          <DrawerHeader className="text-left">
            <DrawerTitle className="p-1">Record Process Checks</DrawerTitle>
            <DrawerDescription className="hidden"></DrawerDescription>
          </DrawerHeader>
          <div className="flex flex-col gap-3 px-5 pb-5">
            <FormField
              datetime
              label="Time of check"
              name="time_of_check"
              required
              control={control}
              disabled={loading}
              errors={errors}
            />
            <FormField
              select
              label="Was coffee turned?"
              name="was_coffee_turned"
              control={control}
              errors={errors}
              disabled={loading}
              placeholder="Select"
              className={'bg-[#FAFAFA]'}
              options={[
                { label: 'Yes', value: true },
                { label: 'No', value: false },
              ]}
            />
            <FormField
              label="pH"
              name="ph_value"
              required
              type="number"
              inputMode="decimal"
              placeholder="Enter pH"
              step="0.01"
              className={'bg-[#FAFAFA]'}
              register={register}
              disabled={loading}
              errors={errors}
            />
            <FormField
              label="Temperature (°C)"
              name="temperature"
              required
              type="number"
              inputMode="decimal"
              className={'bg-[#FAFAFA]'}
              placeholder="Enter temperature"
              step="0.01"
              register={register}
              disabled={loading}
              errors={errors}
            />
            <FormField
              label="Additional notes"
              name="notes"
              textarea
              register={register}
              errors={errors}
              disabled={loading}
              className={'bg-[#FAFAFA]'}
              placeholder="Enter notes"
            />

            {/* Media Uploader */}
            <MediaUploader
              media={media}
              label="Photos/Videos"
              folder={'lot-process-media'}
              onUploaded={fn => (uploadRef.current = fn)}
              className="bg-transparent"
              loading={loading}
            >
              {fields?.map((m, idx) => (
                <div key={idx} className="relative h-[72px] w-[72px] overflow-hidden rounded-xl">
                  {m.mime_type === 'image' ? (
                    <img src={m.public_url} className="h-full w-full object-cover" />
                  ) : (
                    <video src={m.public_url} className="h-full w-full object-cover" muted playsInline />
                  )}

                  <button
                    type="button"
                    onClick={() => onRemoveMedia(m)}
                    className="absolute right-1 top-1 rounded-full bg-white/80 p-0.5"
                    disabled={loading}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </MediaUploader>
          </div>
          <DrawerFooter className={'flex flex-row justify-end gap-2 border-t'}>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false);
                if (media.localMedia.length > 0) {
                  media.setLocalMedia([]);
                }
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spinSlow" />}
              {loading ? 'Saving...' : 'Save Check'}
            </Button>
          </DrawerFooter>
        </form>
      </DrawerContent>
    </Drawer>
  );
};

export default FermentationProcessCheckDrawer;
